// Job 27: TNI / MLF / DLF codes were free-text on the site form, so a typo went
// straight into the comparison maths. This supplies the option lists so they can
// be picked instead.
//
// Only the lists live here. Resolving a code to its factor value already belongs
// to scopingAi's lossFactorService via /api/loss-factors, whose ORDER BY encodes
// the load-before-generation rule a consumption site depends on — the site form
// keeps using it, so there is one such rule rather than two.
//
// Both tables allow reads to any authenticated user (see the note in
// lossFactorRoutes.ts), so a prefix search is a direct, cheap round trip.

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';

// A prefix can match thousands of rows — DLF is keyed per NMI. Cap the scan and
// dedupe client-side; PostgREST has no DISTINCT.
const SCAN_LIMIT = 1000;

/** Unique, sorted, blank-free codes for a dropdown. */
export const dedupeCodes = (values: (string | null | undefined)[]): string[] =>
  [...new Set(values.map((v) => (v ?? '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'en-AU', { numeric: true, sensitivity: 'base' }));

// ilike wildcards typed by a user would widen the scan unpredictably.
const prefix = (term: string): string => `${term.replace(/[%_]/g, '')}%`;

async function fetchCodes(
  table: 'transmission_loss_factor' | 'distribution_loss_factor',
  column: 'tni_code' | 'dlf_code',
  search: string,
): Promise<string[]> {
  let q = supabase.from(table).select(column).limit(SCAN_LIMIT);
  const term = search.trim();
  if (term) q = q.ilike(column, prefix(term));

  const { data, error } = await q;
  if (error) throw error;
  return dedupeCodes((data ?? []).map((row) => (row as Record<string, string | null>)[column]));
}

export const lossFactorCodesService = {
  /** TNI codes — also the key an MLF is looked up by. */
  searchTniCodes: (search: string) => fetchCodes('transmission_loss_factor', 'tni_code', search),
  searchDlfCodes: (search: string) => fetchCodes('distribution_loss_factor', 'dlf_code', search),
};
